import SEOFooter from "@/components/SEOFooter";
import FAQ from "@/components/FAQ";
import JsonLd from "@/components/JsonLd";
import SeoMeta from "@/components/SeoMeta";
import { buildHomePageJsonLd } from "@/data/structuredData";
import Header from "@/components/landing/Header";
import Hero from "@/components/landing/Hero";
import { MarqueeSection, LocationsMarquee } from "@/components/landing/Marquees";
import Services from "@/components/landing/Services";
import FourPillars from "@/components/landing/FourPillars";
import Portfolio from "@/components/landing/Portfolio";
import Testimonials from "@/components/landing/Testimonials";
import ComparisonTable from "@/components/landing/ComparisonTable";
import Pricing from "@/components/landing/Pricing";
import ROICalculator from "@/components/landing/ROICalculator";
import Contact from "@/components/landing/Contact";
import Footer from "@/components/landing/Footer";
import WhatsAppButton from "@/components/landing/WhatsAppButton";
import StickyCTA from "@/components/landing/StickyCTA";
import AIAssistant from "@/components/AIAssistant";
import TrustBar from "@/components/landing/TrustBar";
import UrgencyBadge from "@/components/landing/UrgencyBadge";
import SocialProofToast from "@/components/SocialProofToast";
import AiPhotoEstimator from "@/components/AiPhotoEstimator";
import ExitIntentModal from "@/components/ExitIntentModal";
import LeadMagnetSection from "@/components/landing/LeadMagnetSection";
import JourneyQuiz from "@/components/landing/JourneyQuiz";
import FounderSection from "@/components/landing/FounderSection";
import Guarantees from "@/components/landing/Guarantees";
import CaseStudies from "@/components/landing/CaseStudies";
import Certifications from "@/components/landing/Certifications";
import BookingCalendar from "@/components/landing/BookingCalendar";
import ServiceCatalog from "@/components/landing/ServiceCatalog";
import { useState, useEffect } from "react";
import { Sparkles } from "lucide-react";

export default function LandingPage() {
  const [estimatorOpen, setEstimatorOpen] = useState(false);
  // Global opener so any CTA on the page can trigger the modal.
  useEffect(() => {
    const openHandler = () => setEstimatorOpen(true);
    window.addEventListener("open-photo-estimator", openHandler);
    return () => window.removeEventListener("open-photo-estimator", openHandler);
  }, []);

  return (
    <div className="App">
      <SeoMeta
        title="Kompletný exteriérový realizátor Bratislava | Dom + zateplenie + dlažba | SuperMajstri"
        description="Postavíme dom na kľúč alebo holodom (od 960 €/m²), zateplíme fasádu (od 48 €/m²) a zrealizujeme prístupovú cestu zámkovou dlažbou (od 70 €/m²) — všetko s jedným partnerom. Bratislava, Podunajsko. 3-ročná záruka."
        canonical="https://supermajstri.sk/"
        ogImage="https://supermajstri.sk/og-image.jpg"
        type="website"
      />
      <JsonLd data={buildHomePageJsonLd()} id="home" />
      <UrgencyBadge />
      <Header />
      <main>
        <Hero />
        <TrustBar />
        <FourPillars />
        <FounderSection />
        <MarqueeSection />
        <Services />
        <ServiceCatalog />
        {/* AI Photo Estimator CTA banner */}
        <section className="py-12 bg-gradient-to-b from-[#0a0a0a] to-[#0f0f0f]">
          <div className="max-w-4xl mx-auto px-4">
            <div className="relative overflow-hidden rounded-3xl border border-[#00E5FF]/25 bg-black/40 p-6 sm:p-10">
              <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/8 via-transparent to-[#FF5E00]/6 pointer-events-none" />
              <div className="relative flex flex-col sm:flex-row items-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-[#00E5FF]/15 border border-[#00E5FF]/40 flex items-center justify-center flex-shrink-0">
                  <Sparkles size={28} className="text-[#00E5FF]" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] font-black mb-1">
                    Novinka · Zdarma · Za 30 sekúnd
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight">
                    AI Foto-Odhad Ceny
                  </h3>
                  <p className="text-sm text-zinc-400 mt-2 max-w-lg">
                    Nahrajte fotku vášho priestoru a naša AI vám okamžite vypočíta orientačnú cenu.
                    Nezáväzne, bez čakania na odhadcu.
                  </p>
                </div>
                <button
                  onClick={() => setEstimatorOpen(true)}
                  className="px-6 py-3.5 bg-[#00E5FF] hover:brightness-110 text-black font-black rounded-xl transition-all flex items-center gap-2 whitespace-nowrap"
                  data-testid="open-photo-estimator"
                >
                  <Sparkles size={16} /> Skúsiť teraz
                </button>
              </div>
            </div>
          </div>
        </section>
        <Portfolio />
        <CaseStudies />
        <Guarantees />
        <JourneyQuiz />
        <Testimonials />
        <ComparisonTable />
        <Certifications />
        <Pricing />
        <ROICalculator />
        <FAQ />
        <LeadMagnetSection source="homepage" />
        <LocationsMarquee />
        <BookingCalendar />
        <Contact />
        <SEOFooter />
      </main>
      <Footer />
      <WhatsAppButton />
      <StickyCTA />
      <AIAssistant />
      <SocialProofToast />
      <AiPhotoEstimator open={estimatorOpen} onClose={() => setEstimatorOpen(false)} />
      <ExitIntentModal />
    </div>
  );
}
